import { memo, useCallback, useEffect, useRef, useState } from "react";

import { getAllRoleWorkflows, updateWorkflowRoles } from "@/api/workflow-role";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { HashLoader } from "react-spinners";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface Props {
  workflowId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 10;

const RoleListModal: React.FC<Props> = ({ isOpen, onClose, workflowId }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>انتخاب نقش</DialogTitle>
        </DialogHeader>

        <Content workflowId={workflowId} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};

const Content = memo(
  ({ workflowId, onClose }: { workflowId: number; onClose: () => void }) => {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);

    const observerRef = useRef<IntersectionObserver | null>(null);

    const queryClient = useQueryClient();

    // 🔥 Infinite Query
    const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
      useInfiniteQuery({
        queryKey: ["workflow-roles", workflowId, debouncedSearch],

        queryFn: ({ pageParam = 1 }) =>
          getAllRoleWorkflows(workflowId, {
            page: pageParam,
            size: PAGE_SIZE,
            search: debouncedSearch
          }),

        initialPageParam: 1,

        getNextPageParam: (lastPage, pages) => {
          const totalCount = lastPage?.totalCount ?? 0;

          const loadedItems = pages.flatMap((page) => page?.data || []).length;

          return loadedItems < totalCount ? pages.length + 1 : undefined;
        }
      });

    // 🔥 flatten pages
    const roles = data?.pages.flatMap((page) => page?.data || []) || [];

    // 🔥 init selected roles
    useEffect(() => {
      setSelectedRoles((prev) => {
        const backendSelected = roles
          .filter((x) => x.isAccess)
          .map((x) => x.id);

        return [...new Set([...prev, ...backendSelected])];
      });
    }, [data]);

    // 🔥 mutation
    const { mutate, isPending } = useMutation({
      mutationFn: () => updateWorkflowRoles(workflowId, selectedRoles),

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["workflow-roles", workflowId]
        });

        onClose();
      }
    });

    // 🔥 checkbox toggle
    const changeHandler = (roleId: number) => {
      setSelectedRoles((prev) =>
        prev.includes(roleId)
          ? prev.filter((id) => id !== roleId)
          : [...prev, roleId]
      );
    };

    // 🔥 debounce search
    const handleSearch = useDebouncedCallback((value: string) => {
      setDebouncedSearch(value);
    }, 700);

    // 🔥 infinite scroll observer
    const lastItemRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (isFetchingNextPage) return;

        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        });

        if (node) {
          observerRef.current.observe(node);
        }
      },
      [isFetchingNextPage, hasNextPage, fetchNextPage]
    );

    return (
      <>
        {/* 🔍 SEARCH */}
        <Input
          placeholder="جستجو"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            handleSearch(e.target.value);
          }}
          className="w-full md:w-1/2"
        />

        {/* 📜 LIST */}
        <div className="mt-5 h-64 divide-y divide-slate-300 overflow-y-auto rounded-md border border-slate-300">
          {isFetching && !roles.length ? (
            <div className="flex h-full items-center justify-center">
              <HashLoader color="#0099A5" size={40} />
            </div>
          ) : roles.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-slate-500">نتیجه‌ای یافت نشد</p>
            </div>
          ) : (
            <>
              {roles.map((role, index) => {
                const isLast = index === roles.length - 1;

                return (
                  <div
                    key={role.id}
                    ref={isLast ? lastItemRef : null}
                    className="flex items-center gap-x-2 px-2 py-4"
                  >
                    <Checkbox
                      checked={selectedRoles.includes(role.id)}
                      onCheckedChange={() => changeHandler(role.id)}
                    />

                    <p className="line-clamp-1 text-sm">{role.name}</p>
                  </div>
                );
              })}

              {/* 🔥 loading next page */}
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <HashLoader color="#0099A5" size={25} />
                </div>
              )}
            </>
          )}
        </div>

        {/* 💾 SAVE */}
        <Button
          className="mt-5 w-full"
          disabled={isPending}
          onClick={() => mutate()}
        >
          {isPending ? "در حال ذخیره..." : "ذخیره"}
        </Button>
      </>
    );
  }
);

export default RoleListModal;
